import { Card, CardHeader, CardDescription, CardTitle } from "@/components/ui/card"
import type { LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"

interface StatCardProps {
  title: string
  value: string | number
  description?: string
  icon?: LucideIcon
  trend?: {
    value: number
    direction: "up" | "down"
  }
  className?: string
}

/**
 * Stat card component for displaying metrics
 *
 * Shows a metric with optional icon, description, and trend indicator.
 * Commonly used on dashboard pages for key statistics.
 *
 * @example
 * <StatCard
 *   title="Total Users"
 *   value={1234}
 *   icon={Users}
 *   description="Active accounts"
 *   trend={{ value: 12, direction: "up" }}
 * />
 */
export function StatCard({
  title,
  value,
  description,
  icon: Icon,
  trend,
  className
}: StatCardProps) {
  return (
    <Card className={cn("", className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardDescription>{title}</CardDescription>
          {Icon && (
            <Icon className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
        <CardTitle className="text-3xl font-bold">{value}</CardTitle>
        {(description || trend) && (
          <div className="flex items-center gap-2 text-xs">
            {trend && (
              <span className={cn(
                "font-medium",
                trend.direction === "up"
                  ? "text-success-600"
                  : "text-destructive"
              )}>
                {trend.direction === "up" ? "+" : "-"}
                {Math.abs(trend.value)}%
              </span>
            )}
            {description && (
              <span className="text-muted-foreground">{description}</span>
            )}
          </div>
        )}
      </CardHeader>
    </Card>
  )
}
